import { localized } from "@msuite/ui/localization";
import { createColumnHelper } from "@msuite/ui/table";
import { OrderStateTag } from "@msuite/ui/tag";
import { moment } from "@msuite/utils/date";
import { guardString } from "@msuite/utils/strings";
import type { OrderStateTransitionListItem } from "@msuite/utils/types";

const columnHelper = createColumnHelper<OrderStateTransitionListItem>();

/**
 * The protocol as a table: one row per state change, newest first.
 *
 * `level` is the first column and is hidden by the round's own protocol through
 * `initialColumnVisibility` — there every row is the same round, so the column would repeat itself.
 * One column set rather than two, because that is the difference between the two views and the
 * table already knows how to drop a column.
 */
export const stateTransitionsTableColumns = [
	columnHelper.accessor(
		(row) =>
			row.suborderId === null
				? localized("stock.protocol_level_order")
				: localized("stock.protocol_level_round", {
						number: String(row.suborderNumber ?? "--"),
					}),
		{
			id: "level",
			header: localized("general.type"),
			cell: (cell) => cell.getValue(),
		},
	),
	// A creation carries no origin, so the cell is a dash rather than an empty tag — the difference
	// between "raised as pending" and "moved back to pending" is the whole reason the column exists.
	columnHelper.accessor("fromState", {
		id: "fromState",
		header: localized("stock.protocol_from_state"),
		cell: (cell) => {
			const fromState = cell.getValue();
			if (fromState === null) return guardString(null);
			return <OrderStateTag state={fromState} />;
		},
	}),
	columnHelper.accessor("toState", {
		id: "toState",
		header: localized("stock.protocol_to_state"),
		cell: (cell) => <OrderStateTag state={cell.getValue()} />,
	}),
	columnHelper.accessor("changedAt", {
		id: "changedAt",
		header: localized("general_fields.created_at"),
		cell: (cell) => moment(cell.getValue()).shortFormat(),
	}),
	// Null once the user is deleted or where the write carried no claims, and the row cannot tell
	// those apart — so it names nobody rather than guessing.
	columnHelper.accessor("changedByName", {
		id: "changedByName",
		header: localized("general_fields.created_by"),
		cell: (cell) => guardString(cell.getValue()),
	}),
];
